<template>
    <div>
        <div>
            <v-col cols="12" xl="10">
                <v-row>
                    <v-col cols="12">
                        <h5 class="patient-form-title mb-0">{{ t('patients::form.title.general_information') }}</h5>
                    </v-col>
                    <v-col v-if="duplicatedPatientError" cols="12">
                        <b-alert type="error" :message="duplicatedPatientError"> </b-alert>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-input
                            to-mutate="patients::patient.surname"
                            :label="t('patients::form.label.surname')"
                            :rules="validationRules.surname"
                            mandatory
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-input
                            to-mutate="patients::patient.forename"
                            :label="t('patients::form.label.forename')"
                            mandatory
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-date-picker-store
                            to-mutate="patients::patient.birth_date"
                            :label="t('patients::form.label.birth_date')"
                            :rules="validationRules.birth_date"
                            mandatory
                        ></b-date-picker-store>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-input
                            to-mutate="patients::patient.nhs_id"
                            :label="t('patients::form.label.nhs_id')"
                            persistent-hint
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-select
                            to-mutate="patients::patient.gender"
                            :label="t('patients::form.label.gender')"
                            :select-items="genderSelectItems"
                            :rules="validationRules.gender"
                            mandatory
                        ></b-select>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-select
                            to-mutate="patients::patient.race"
                            :label="t('patients::form.label.race')"
                            :select-items="raceSelectItems"
                            :rules="validationRules.race"
                            mandatory
                        ></b-select>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" xl="10" class="mt-10">
                <b-switch
                    class="switch"
                    to-mutate="patients::patient.dont_send_mails"
                    :label="t('patients::form.label.dont_send_mails')"
                ></b-switch>
            </v-col>
            <v-col cols="12" xl="10" class="mt-10">
                <v-row>
                    <v-col cols="12">
                        <h5 class="patient-form-title mb-0">{{ t('patients::form.title.pharmacy_n_consultant') }}</h5>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-select
                            to-mutate="patients::patient.pharmacy_site_id"
                            :label="
                                !pharmaciesOptions.length
                                    ? t('patients::form.label.loading_options')
                                    : t('patients::form.label.pharmacy_site')
                            "
                            :select-items="pharmaciesOptions"
                            :readonly="hasPharmacySite || !pharmaciesOptions.length"
                            mandatory
                            component-name="v-autocomplete"
                        >
                            <template #item="{ item, on, attrs }">
                                <v-list-item v-bind="attrs" v-on="on">
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ item.text }}
                                            <span v-if="!item.is_registered">&nbsp;[DEREGISTERED]</span>
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                            <template #selection="{ item, on, attrs }">
                                <v-chip v-bind="attrs" v-on="on">
                                    {{ item.text }}
                                    <span v-if="!item.is_registered">&nbsp;[DEREGISTERED]</span>
                                </v-chip>
                            </template>
                            <template v-if="patient.pharmacy_site" #append-outer>
                                <relation-info-modal :data="pharmacySite"></relation-info-modal>
                            </template>
                        </b-select>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-select
                            to-mutate="patients::patient.consultant_id"
                            :label="
                                !consultantsOptions.length
                                    ? t('patients::form.label.loading_options')
                                    : t('patients::form.label.consultant')
                            "
                            :select-items="consultantsOptions"
                            :readonly="!consultantsOptions.length"
                            :rules="validationRules.consultant_id"
                            mandatory
                            component-name="v-autocomplete"
                        >
                            <template v-if="patient.consultant" #append-outer>
                                <relation-info-modal :data="consultant"></relation-info-modal>
                            </template>
                        </b-select>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-select
                            to-mutate="patients::patient.collection_centre_id"
                            :label="
                                !collectionCentresOptions.length
                                    ? t('patients::form.label.loading_options')
                                    : t('patients::form.label.collection_centre')
                            "
                            :select-items="collectionCentresOptions"
                            :readonly="!collectionCentresOptions.length"
                            component-name="v-autocomplete"
                        >
                            <template v-if="patient.collection_center" #append-outer>
                                <relation-info-modal :data="collectionCenter"></relation-info-modal>
                            </template>
                        </b-select>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" xl="10" class="mt-10">
                <v-row>
                    <v-col cols="12">
                        <h5 class="patient-form-title mb-0">{{ t('patients::form.title.address') }}</h5>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-input
                            to-mutate="patients::patient.address"
                            :label="t('patients::form.label.address')"
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-input
                            to-mutate="patients::patient.second_address"
                            :label="t('patients::form.label.address_line')"
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="py-0">
                        <b-input
                            to-mutate="patients::patient.post_code"
                            :label="t('patients::form.label.post_code')"
                        ></b-input>
                    </v-col>

                    <v-col cols="12" lg="4" class="pt-0">
                        <b-input
                            clearable
                            to-mutate="patients::patient.country"
                            :label="t('patients::form.label.country')"
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="pt-0">
                        <b-input
                            clearable
                            to-mutate="patients::patient.region"
                            :label="t('patients::form.label.county')"
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="pt-0">
                        <b-input
                            clearable
                            to-mutate="patients::patient.town"
                            :label="t('patients::form.label.town')"
                        ></b-input>
                    </v-col>
                </v-row>
            </v-col>
        </div>
    </div>
</template>

<script>
import RelationInfoModal from './RelationInfoModal.vue';
import BAlert from '../../../../../../../bgo/bgo-components/resources/js/components/BAlert.vue';

export default {
    components: { BAlert, RelationInfoModal },
    data() {
        return {
            validationRules: {
                surname: [(v) => !!v || t('users::validation.field.required')],
                forename: [(v) => !!v || t('users::validation.field.required')],
                gender: [(v) => !!v || t('users::validation.field.required')],
                race: [(v) => !!v || t('users::validation.field.required')],
                // nhs_id: [this.$root.rules.integer],
                birth_date: [
                    (v) => !!v || t('users::validation.field.required'),
                    (v) => {
                        if (v) {
                            const selectDate = v.split('.');
                            const selectFormatDate = `${selectDate[2]}-${selectDate[1]}-${selectDate[0]}`;
                            // const selctetDate = moment(selectFormatDate).toDate();
                            const currentDate = moment().toDate();
                            return (
                                moment(selectFormatDate).isBefore(currentDate) ||
                                t('users::validation.field.date_in_past')
                            );
                        }
                        return false || t('users::validation.field.date_in_past');
                    },
                ],
                fax: [this.$root.rules.fax],
                consultant_id: [(v) => !!v || t('users::validation.field.required')],
            },
            genderSelectItems: [
                { text: 'Male', value: 'male' },
                { text: 'Female', value: 'female' },
                { text: 'Other', value: 'other' },
            ],
            raceSelectItems: [
                { text: 'Caucasian', value: 'caucasian' },
                { text: 'Asian', value: 'asian' },
                { text: 'Afro-Caribbean', value: 'afro-caribbean' },
                { text: 'Mixed', value: 'mixed' },
                { text: 'Other', value: 'other' },
            ],
        };
    },
    computed: {
        loadingPharmacyOptions() {
            return this.pharmaciesOptions.length === 0;
        },
        duplicatedPatientError() {
            if (
                this.$store.state.patients.errorMessages.patient &&
                this.$store.state.patients.errorMessages.patient.id &&
                this.$store.state.patients.errorMessages.patient.id[0]
            ) {
                return this.$store.state.patients.errorMessages.patient.id[0];
            }
            return false;
        },
        patient() {
            return this.$store.state.patients.patient;
        },
        pharmaciesOptions() {
            return this.$store.state.patients.pharmaciesOptions.map((x) => ({
                value: x.id,
                text: x.name,
                is_registered: x.is_registered,
            }));
        },
        consultantsOptions() {
            return this.$store.state.patients.consultantsOptions.map((x) => ({ value: x.id, text: x.name }));
        },
        collectionCentresOptions() {
            return this.$store.state.patients.collectionCentresOptions.map((x) => ({ value: x.id, text: x.name }));
        },
        hasPharmacySite() {
            return this.$store.state.patients.hasPharmacySite;
        },
        consultant() {
            if (!this.patient || !this.patient.consultant) {
                return {};
            }

            return {
                Forename: this.patient.consultant.forename,
                Surname: this.patient.consultant.surname,
                'Gmc Number': this.patient.consultant.gmc_number,
                'Company Name': this.patient.consultant.company_name,
                'Dms Id': this.patient.consultant.dms_id,
                'Can Be Sent Letters': this.patient.consultant.can_be_sent_letters ? 'Yes' : 'No',
                'Is RCOP Member': this.patient.consultant.is_rcop_member ? 'Yes' : 'No',
                'Is Registered': this.patient.consultant.is_registered ? 'Yes' : 'No',
                'Specialist Register Of Psychiatrists': this.patient.consultant.specialist_register_of_psychiatrists
                    ? 'Yes'
                    : 'No',
                Address: this.patient.consultant.address,
                'Post Code': this.patient.consultant.post_code,
                Phone: this.patient.consultant.phone,
                Fax: this.patient.consultant.fax,
                Email: this.patient.consultant.email,
                'Out Of Hours Contact Name': this.patient.consultant.out_of_hours_contact_name,
                'Out Of Hours Contact Phone': this.patient.consultant.out_of_hours_contact_phone,
                'Out Of Hours Contact Fax': this.patient.consultant.out_of_hours_contact_fax,
                'Out Of Hours Contact Email': this.patient.consultant.out_of_hours_contact_email,
            };
        },
        collectionCenter() {
            if (!this.patient || !this.patient.collection_center) {
                return {};
            }

            return {
                Prn: this.patient.collection_center.prn,
                Name: this.patient.collection_center.name,
                Email: this.patient.collection_center.email,
                Operator: this.patient.collection_center.operator,
                'Dms Id': this.patient.collection_center.dms_id,
                Address: this.patient.collection_center.address,
                'Contacts Information': this.patient.collection_center.contacts_information,
                'Courier Details': this.patient.collection_center.courier_details,
                'Courier Phone': this.patient.collection_center.courier_phone,
                'Post Code': this.patient.collection_center.post_code,
                Phone: this.patient.collection_center.phone,
                Fax: this.patient.collection_center.fax,
                'Is Patient Mode': this.patient.collection_center.is_patient_mode ? 'Yes' : 'No',
                'Visible In Qc List': this.patient.collection_center.visible_in_qc_list ? 'Yes' : 'No',
                'Is Registered': this.patient.collection_center.is_registered ? 'Yes' : 'No',
            };
        },
        pharmacySite() {
            if (!this.patient || !this.patient.pharmacy_site) {
                return {};
            }

            return {
                'Dms Id': this.patient.pharmacy_site.dms_id,
                'Site Name': this.patient.pharmacy_site.site_name,
                Email: this.patient.pharmacy_site.email,
                Address: this.patient.pharmacy_site.address,
                'Post Code': this.patient.pharmacy_site.post_code,
                Phone: this.patient.pharmacy_site.phone,
                Fax: this.patient.pharmacy_site.fax,
                'Chief Pharmacist Name': this.patient.pharmacy_site.chief_pharmacist_name,
                'Chief Pharmacist Phone': this.patient.pharmacy_site.chief_pharmacist_phone,
                'Chief Pharmacist Fax': this.patient.pharmacy_site.chief_pharmacist_fax,
                'Chief Pharmacist Email': this.patient.pharmacy_site.chief_pharmacist_email,
                'Chief Pharmacist Rpsgb Number': this.patient.pharmacy_site.chief_pharmacist_rpsgb_number,
                'Technician Name': this.patient.pharmacy_site.technician_name,
                'Technician Phone': this.patient.pharmacy_site.technician_phone,
                'Technician Fax': this.patient.pharmacy_site.technician_fax,
                'Technician Email': this.patient.pharmacy_site.technician_email,
                'Technician Rpsgb Number': this.patient.pharmacy_site.technician_rpsgb_number,
                'Out Of Hours Contact Name': this.patient.pharmacy_site.out_of_hours_contact_name,
                'Out Of Hours Contact Phone': this.patient.pharmacy_site.out_of_hours_contact_phone,
                'Out Of Hours Contact Fax': this.patient.pharmacy_site.out_of_hours_contact_fax,
                'Out Of Hours Contact Email': this.patient.pharmacy_site.out_of_hours_contact_email,
                'Is Registered': this.patient.pharmacy_site.is_registered ? 'Yes' : 'No',
            };
        },
    },
    created() {},
};
</script>

<style></style>
